import React, { useState, useEffect, useRef, useContext } from 'react';
import { CaseStudyColorContext } from '../pages/CaseStudy';
import Lottie from 'lottie-react';
import playAnimation from '../assets/lottie_animations/play.json';
import playHoverAnimation from '../assets/lottie_animations/play hover.json';
import playHoverOutAnimation from '../assets/lottie_animations/play hover out.json';
import playOutAnimation from '../assets/lottie_animations/play out.json';
import './Grid.css';

function GridItem({ item, index }) {
  const [, setIsPlaying] = useState(item.type === 'video' ? item.autoplay : false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);
  const videoRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const { background, font } = useContext(CaseStudyColorContext);

  useEffect(() => {
    const video = videoRef.current;
    if (item.type !== 'video' || !video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (item.autoplay) {
            video.play().catch(err => {
              console.error('Autoplay prevented:', err);
            });
          }
        } else {
          video.pause();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, [item.type, item.autoplay]);

  const togglePlayPause = () => {
    if (item.type !== 'video') return;
    
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
      setHasClicked(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleLottieComplete = () => {
    if (!hasPlayedOnce) {
      setHasPlayedOnce(true);
    }
  };

  const getLottieAnimation = () => {
    // Only show animation for non-autoplay videos
    if (item.type !== 'video' || item.autoplay) {
      return null;
    }

    if (hasClicked) {
      return playOutAnimation;
    } else if (hasPlayedOnce) {
      if (isHovered) {
        return playHoverAnimation;
      } else {
        return playHoverOutAnimation;
      }
    } else {
      return playAnimation;
    }
  };

  const currentAnimation = getLottieAnimation();

  if (item.type === 'video') {
    return (
      <div 
        className="grid-item grid-item-video"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={togglePlayPause}
        style={{ position: 'relative', cursor: 'pointer' }}
      >
        <video 
          ref={videoRef}
          src={item.video?.asset?.url || item.video?.url}
          poster={item.poster?.asset?.url || item.poster?.url}
          playsInline
          loop={item.autoplay}
          muted={item.autoplay}
          onPlay={() => setIsPlaying(true)}
          onPause={() => {
            setIsPlaying(false);
            setHasClicked(false);
          }}
          onEnded={() => {
            setIsPlaying(false);
            setHasClicked(false);
          }}
          className="grid-media"
        />
        
        {/* Lottie Animation Overlay for non-autoplay videos */}
        {currentAnimation && (
          <div 
            className="grid-lottie-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 10
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: 'auto',
              maxWidth: '400px'
            }}>
              <Lottie
                animationData={currentAnimation}
                style={{
                  width: '100%',
                  height: 'auto'
                }}
                loop={false}
                autoplay={true}
                onComplete={handleLottieComplete}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid-item grid-item-image">
      <img
        src={item.image?.asset?.url || item.image?.url}
        alt={item.alt}
        className="grid-media"
      />
    </div>
  );
}

function Grid({ module }) {
  const items = module?.items || [];
  
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className="grid-module">
      <div className="grid-container">
        {items.map((item, index) => (
          <GridItem
            key={index}
            item={item}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

export default Grid;

